import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  InputAdornment,
  Tooltip,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import { AiOutlineClose, AiOutlineSearch } from "react-icons/ai";
import { PiFileCsv } from "react-icons/pi";
import { useQuery } from "@tanstack/react-query";
import { fetchGroupedServicesDetails, fetchMatchedSofDocs } from "../../api/dashboardApi";
import dayjs from "dayjs";
import MatchedSofDocsModal from "./MatchedSofDocsModal";
import DocumentViewer from "../DocumentHub/DocumentViewer";
import ContractAmountBreakdownModal from "./ContractAmountBreakdownModal";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{
        flex: value === index ? 1 : 0,
        display: value === index ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {value === index && (
        <Box sx={{ p: 1, flex: 1, overflowY: "auto" }}>{children}</Box>
      )}
    </div>
  );
}
 
const GroupedServicesDetailsModal = ({
  open,
  onClose,
  initialFilters,
  searchFor,
  formatNumber,
}) => {
  // Search and Pagination states
  const [serviceIdSearch, setServiceIdSearch] = useState("");
  const [invoiceIdSearch, setInvoiceIdSearch] = useState("");
  const [classificationSearch, setClassificationSearch] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [csvLoading, setCsvLoading] = useState(false);

  const [sofModalOpen, setSofModalOpen] = useState(false);
  const [sofModalData, setSofModalData] = useState(null);
  const [sofModalLoading, setSofModalLoading] = useState(false);

  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerUrl, setDocViewerUrl] = useState("");

  const [openBreakdown, setOpenBreakdown] = useState(false);
  const [breakdownData, setBreakdownData] = useState({});

  const handleInvoiceClick = (url, uploaded_on_s3) => {
    if (uploaded_on_s3 === 1 && url) {
      setDocViewerUrl(url);
      setDocViewerOpen(true);
    }
  };

  const handleClassificationClick = async (serviceId, invoiceId) => {
    if (!serviceId || !invoiceId) return;
    setSofModalOpen(true);
    setSofModalLoading(true);
    try {
      const data = await fetchMatchedSofDocs(serviceId, invoiceId);
      setSofModalData(data);
    } catch (err) {
      console.error("Failed to fetch matched SOF docs", err);
      setSofModalData({ status: false, msg: "Failed to fetch data." });
    } finally {
      setSofModalLoading(false);
    }
  };
 
  React.useEffect(() => {
    if (open) {
      setServiceIdSearch("");
      setInvoiceIdSearch(initialFilters.invoice_id || "");
      setClassificationSearch("");
      setStartDateFilter(initialFilters.start_date ? dayjs(initialFilters.start_date).format("YYYY-MM-DD") : "");
      setEndDateFilter(initialFilters.end_date ? dayjs(initialFilters.end_date).format("YYYY-MM-DD") : "");
      setPage(0);
    }
  }, [open, initialFilters]);
 
  const handleReset = () => {
    setServiceIdSearch("");
    setInvoiceIdSearch(initialFilters.invoice_id || "");
    setClassificationSearch("");
    setStartDateFilter(initialFilters.start_date ? dayjs(initialFilters.start_date).format("YYYY-MM-DD") : "");
    setEndDateFilter(initialFilters.end_date ? dayjs(initialFilters.end_date).format("YYYY-MM-DD") : "");
    setPage(0);
  };
 
  const currentFilters = {
    start_date: startDateFilter || undefined,
    end_date: endDateFilter || undefined,
    vendor_id: initialFilters.vendor_id,
    location_id: initialFilters.location_id,
    search_for: searchFor,
    service_id: serviceIdSearch || undefined,
    invoice_id: invoiceIdSearch || undefined,
    classification: classificationSearch || undefined,
    page: page + 1,
    limit: rowsPerPage
  };
 
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["groupedServicesDetails", currentFilters],
    queryFn: () => fetchGroupedServicesDetails(currentFilters),
    enabled: open,
  });
 
  const detailsData = data?.data || [];
  const totalCounts = data?.total_counts || 0;
 
  const handleDownloadCSV = async () => {
    if (totalCounts === 0) return;
 
    setCsvLoading(true);
    try {
      const csvFilters = {
        ...currentFilters,
        limit: "all",
        page: undefined
      };
      
      const response = await fetchGroupedServicesDetails(csvFilters);
      const allData = response?.data || [];
      
      if (!allData.length) return;
 
      // Define columns for CSV
      const headers = [
        "Service ID",
        "Invoice ID",
        "Invoice Date",
        "Location ID",
        "Service Name",
        "Matched Doc ID",
        "Referred Doc ID",
        "Classification",
        "Classification Description",
        "Contract Amount",
        "Invoice Currency",
        "Invoice Conv. Rate",
        "Invoice Amount",
        "Discrepancy Amount"
      ];
      const csvRows = [headers.join(",")];
 
      allData.forEach(row => {
        const values = [
          `"${row.service_id || ""}"`,
          `"${row.invoice_id || ""}"`,
          `"${row.invoice_date || ""}"`,
          `"${row.location_id || ""}"`,
          `"${(row.service_name || "").replace(/"/g, '""')}"`,
          `"${row.matched_doc_id || ""}"`,
          `"${row.refered_doc_id || ""}"`,
          `"${(row.discrepancy_classification || "").replace(/_/g, ' ')}"`,
          `"${(row.discrepancy_classification_description || "").replace(/"/g, '""')}"`,
          row.contract_amount || 0,
          `"${row.invoice_currency || ""}"`,
          row.invoice_conversion_rate != null ? Number(row.invoice_conversion_rate).toFixed(3) : "",
          row.amount_usd || 0,
          row.discrepancy_amount || 0
        ];
        csvRows.push(values.join(","));
      });
 
      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      
      const filePrefix = searchFor === 'overbilled' ? 'Overbilled'
        : searchFor === 'underbilled' ? 'Underbilled' : 'All_Discrepant';
        
      link.setAttribute("download", `${filePrefix}_Services_Details_${dayjs().format("YYYYMMDD")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download CSV:", error);
    } finally {
      setCsvLoading(false);
    }
  };
 
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
 
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Calculate total discrepancy for header
  const totalDiscrepancy = detailsData.reduce((sum, item) => sum + (Number(item.discrepancy_amount) || 0), 0);

  // Calculate unique counts
  const uniqueServiceIds = new Set(detailsData.map(item => item.service_id).filter(Boolean)).size;
  const uniqueInvoiceIds = new Set(detailsData.map(item => item.invoice_id).filter(Boolean)).size;
  const uniqueLocationIds = new Set(detailsData.map(item => item.location_id).filter(Boolean)).size;
  
  const titleText = searchFor === 'overbilled' ? 'Overbilled Services Details'
                  : searchFor === 'underbilled' ? 'Underbilled Services Details'
                  : searchFor === 'ARTIFACT_UNRESOLVED_CONTRACT' ? 'Due to Missing Contract Details'
                  : 'Grouped Services Discrepancy Details';
 
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          textTransform: "capitalize"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {titleText}
          {/* {uniqueServiceIds > 0 && (
            <Tooltip title="Unique Service IDs" placement="top" arrow>
              <Box
                component="span"
                sx={{
                  backgroundColor: "#F8EAFF",
                  color: "#7C2EA0",
                  fontSize: "0.85rem",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  border: "1px solid #7C2EA0",
                }}
              >
                {uniqueServiceIds} Services
              </Box>
            </Tooltip>
          )}
          {uniqueInvoiceIds > 0 && (
            <Tooltip title="Unique Invoice IDs" placement="top" arrow>
              <Box
                component="span"
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  fontSize: "0.85rem",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  border: "1px solid #1976d2",
                }}
              >
                {uniqueInvoiceIds} Invoices
              </Box>
            </Tooltip>
          )}
          {uniqueLocationIds > 0 && (
            <Tooltip title="Unique Location IDs" placement="top" arrow>
              <Box
                component="span"
                sx={{
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  fontSize: "0.85rem",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  border: "1px solid #2e7d32",
                }}
              >
                {uniqueLocationIds} Locations
              </Box>
            </Tooltip>
          )} */}
        </Box>
        <IconButton onClick={onClose}>
          <AiOutlineClose />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* 🔹 Filter Bar */}
        <Box
          sx={{
            mb: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-end",
            flexWrap: "nowrap",
          }}
        >
          <TextField
            size="small"
            type="date"
            value={startDateFilter}
            onChange={(e) => { setStartDateFilter(e.target.value); setPage(0); }}
            sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "13px" } }}
          />
          <TextField
            size="small"
            type="date"
            value={endDateFilter}
            onChange={(e) => { setEndDateFilter(e.target.value); setPage(0); }}
            sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "13px" } }}
          />
          <TextField
            size="small"
            placeholder="Service ID..."
            value={serviceIdSearch}
            onChange={(e) => {
              setServiceIdSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AiOutlineSearch size={18} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            placeholder="Invoice ID..."
            value={invoiceIdSearch}
            onChange={(e) => {
              setInvoiceIdSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AiOutlineSearch size={18} />
                </InputAdornment>
              ),
            }}
          />
          {searchFor !== 'ARTIFACT_UNRESOLVED_CONTRACT' && (
            <TextField
              size="small"
              placeholder="Classification..."
              value={classificationSearch}
              onChange={(e) => {
                setClassificationSearch(e.target.value);
                setPage(0);
              }}
              sx={{
                minWidth: 150,
                "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AiOutlineSearch size={18} />
                  </InputAdornment>
                ),
              }}
            />
          )}
 
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {(serviceIdSearch !== "" || invoiceIdSearch !== "" || classificationSearch !== "" || startDateFilter !== (initialFilters.start_date ? dayjs(initialFilters.start_date).format("YYYY-MM-DD") : "") || endDateFilter !== (initialFilters.end_date ? dayjs(initialFilters.end_date).format("YYYY-MM-DD") : "")) && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    paddingInline: "10px",
                    height: "32px",
                  }}
                >
                  Reset
                </Button>
              )}
              
              <Tooltip title="Download CSV" placement="top" arrow>
                <span
                  onClick={csvLoading ? undefined : handleDownloadCSV}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: totalCounts > 0 && !csvLoading ? "pointer" : "default",
                    opacity: totalCounts > 0 && !csvLoading ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (totalCounts > 0 && !csvLoading) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  {csvLoading ? <CircularProgress size={16} sx={{ color: "#333" }} /> : <PiFileCsv size={18} color="#333" />}
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
 
        <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {searchFor === 'ARTIFACT_UNRESOLVED_CONTRACT' && (
            <Box sx={{ px: 3, pt: 1, pb: 1 }}>
              <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Classification: ARTIFACT UNRESOLVED CONTRACT</Typography>
                <Typography variant="caption">
                  {detailsData.length > 0 && detailsData[0].discrepancy_classification_description
                    ? detailsData[0].discrepancy_classification_description
                    : "Due to missing contract documentation."}
                </Typography>
              </Alert>
            </Box>
          )}
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {error?.message || "Failed to load services details."}
              </Typography>
            </Box>
          ) : detailsData.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "50vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Service ID</TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Invoice ID</TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Invoice Date</TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Location ID</TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Service Name</TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Matched Doc ID</TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Referred Doc ID</TableCell>
                      {searchFor !== 'ARTIFACT_UNRESOLVED_CONTRACT' && (
                        <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Classification</TableCell>
                      )}
                      <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Contract Amount</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Invoice Currency</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Invoice Conv. Rate</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Invoice Amount</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11 }}>Discrepancy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailsData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          <span 
                            onClick={() => handleClassificationClick(row.service_id, row.invoice_id)}
                            style={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0", color: "#1976d2" }}
                          >
                            {row.service_id}
                          </span>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, color: "#1976d2" }}>
                          {row.uploaded_on_s3 === 1 && row.invoice_file_url ? (
                            <span
                              onClick={() => handleInvoiceClick(row.invoice_file_url, row.uploaded_on_s3)}
                              style={{ cursor: "pointer", borderBottom: "1px dashed #1976d2" }}
                            >
                              {row.invoice_id}
                            </span>
                          ) : (
                            <span style={{ color: "rgba(0,0,0,0.87)" }}>{row.invoice_id}</span>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{row.invoice_date || "-"}</TableCell>
                        <TableCell>{row.location_id || "-"}</TableCell>
                        <TableCell sx={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.service_name}>
                          {row.service_name || "Unknown Service"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {row.matched_doc_id ? (
                            row.matched_doc_pdf_url ? (
                              <span
                                onClick={() => handleInvoiceClick(row.matched_doc_pdf_url, 1)}
                                style={{ cursor: "pointer", color: "#1976d2", borderBottom: "1px dashed #1976d2" }}
                              >
                                {row.matched_doc_id}
                              </span>
                            ) : (
                              <span>{row.matched_doc_id}</span>
                            )
                          ) : (
                            <span style={{ color: "#999" }}>—</span>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {row.refered_doc_id || <span style={{ color: "#999" }}>—</span>}
                        </TableCell>
                        {searchFor !== 'ARTIFACT_UNRESOLVED_CONTRACT' && (
                          <TableCell>
                            {(() => {
                              const rawClassification = row.discrepancy_classification || "-";
                              const classification = rawClassification !== "-" ? rawClassification.replace(/_/g, ' ') : "-";
                              const description = row.discrepancy_classification_description || classification;
                              return classification !== "-" ? (
                                <Tooltip title={description} arrow placement="top">
                                  <span style={{ cursor: "help", borderBottom: "1px dashed #7C2EA0" }}>
                                    {classification}
                                  </span>
                                </Tooltip>
                              ) : (
                                classification
                              );
                            })()}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          {(row.contract_amount_breakdown && ((row.contract_amount_breakdown.sof_items && row.contract_amount_breakdown.sof_items.length > 0) || (row.contract_amount_breakdown.amendment_items && row.contract_amount_breakdown.amendment_items.length > 0))) ? (
                            <span 
                              style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                              onClick={() => {
                                const rawBreakdown = {
                                  sof: row.contract_amount_breakdown.sof_items || [],
                                  amendment: row.contract_amount_breakdown.amendment_items || [],
                                  contract_calculation: row.contract_amount_breakdown.contract_calculation || null,
                                  total_sof: row.contract_amount_breakdown.total_sof,
                                  total_amendment: row.contract_amount_breakdown.total_amendment,
                                  contract_amount: row.contract_amount,
                                  discrepancy_explanation: row.discrepancy_explanation || null,
                                };
                                setBreakdownData(rawBreakdown);
                                setOpenBreakdown(true);
                              }}
                            >
                              {row.contract_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : (
                            row.contract_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {row.invoice_currency || <span style={{ color: "#999" }}>—</span>}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {row.invoice_conversion_rate != null ? (
                            <Tooltip title={row.invoice_conversion_rate} placement="top" arrow>
                              <span style={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0" }}>
                                {Number(row.invoice_conversion_rate).toFixed(3)}
                              </span>
                            </Tooltip>
                          ) : (
                            <span style={{ color: "#999" }}>—</span>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {(row.invoice_amount_breakdown && row.invoice_amount_breakdown.length > 0) ? (
                            <Tooltip
                              title={
                                <Box sx={{ p: 0.5, fontSize: "12px", minWidth: 200, maxHeight: "300px", overflowY: "auto", pr: 1, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '4px' } }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>Breakdown</Typography>
                                  {row.invoice_amount_breakdown.map((item, i) => (
                                    <Box key={`inv-${i}`} sx={{ mb: 1.5, borderBottom: "1px dashed #ccc", pb: 1.5 }}>
                                      <Typography variant="caption" sx={{ display: "block", wordBreak: "break-word", mb: 0.5 }}>
                                        Service Name: <strong>{item.service_name || "Unknown Service"}</strong>
                                      </Typography>
                                      
                                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
                                        <Typography variant="caption">
                                          Charge Type: <strong>{item.charge_type || "N/A"}</strong>
                                        </Typography>
                                        <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                          Amt Before Disc (USD): <strong>{item.amount_before_discount_usd !== undefined && item.amount_before_discount_usd !== null ? Number(item.amount_before_discount_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}</strong>
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                          Discount (USD): <strong>{item.discount_usd !== undefined && item.discount_usd !== null ? Number(item.discount_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}</strong>
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                          Amount (USD): <strong>{Number(item.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                        </Typography>
                                      </Box>

                                      <Typography sx={{ display: "block", fontSize: "10px", wordBreak: "break-word", lineHeight: 1.3 }}>
                                        Description: {item.service_description || "N/A"}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              }
                              arrow
                              placement="left"
                            >
                              <span style={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0" }}>
                                {row.amount_usd?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </Tooltip>
                          ) : (
                            row.amount_usd?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: Number(row.discrepancy_amount) > 0 ? '#d32f2f' : '#2e7d32' }}>
                          {row.discrepancy_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 50, 100, 200, 500, 1000]}
                component="div"
                count={totalCounts}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <Typography variant="body1" color="textSecondary" sx={{ fontStyle: "italic" }}>
                No data available matching your search.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      {/* Breakdown Modal */}
      <ContractAmountBreakdownModal 
        open={openBreakdown}
        onClose={() => setOpenBreakdown(false)}
        breakdownData={breakdownData}
      />
      <MatchedSofDocsModal
        open={sofModalOpen}
        onClose={() => setSofModalOpen(false)}
        data={sofModalData}
        isLoading={sofModalLoading}
      />
      <DocumentViewer
        open={docViewerOpen}
        url={docViewerUrl}
        loading={false}
        onClose={() => setDocViewerOpen(false)}
        onIframeLoad={() => {}}
      />
    </Dialog>
  );
};
 
export default GroupedServicesDetailsModal;
